import React, { useState } from 'react';
import { ContactFormData, ContactResponse, ValidationErrors } from '../types/api';
import { ContactApi } from '../services/contactApi';
import { validateContactForm, hasValidationErrors } from '../utils/validation';

const ContactForm: React.FC = () => {
    // Form data state
    const [formData, setFormData] = useState<ContactFormData>({
        name: '',
        email: '',
        message: '',
    });

    // UI state
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [errors, setErrors] = useState<ValidationErrors>({});
    const [serverResponse, setServerResponse] = useState<ContactResponse | null>(null);

    // Handle form input changes
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        // Update form data
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));

        // Clear validation error for this field
        if (errors[name as keyof ValidationErrors]) {
            setErrors(prev => ({
                ...prev,
                [name]: undefined,
            }));
        }
    };

    // Handle form submission
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        console.log('FORMULÄR SKICKAT!');
        console.log('Form data:', formData);

        // Client-side validation
        const validationErrors = validateContactForm(formData);
        if (hasValidationErrors(validationErrors)) {
            console.log('Validering misslyckades:', validationErrors);
            setErrors(validationErrors);
            return;
        }

        console.log('Validering OK - skickar till backend...');
        setIsSubmitting(true);
        setErrors({});

        try {
            // Submit to backend
            const response = await ContactApi.submitContact(formData);

            console.log('Svar från backend:', response);
            setServerResponse(response);
            setIsSubmitted(true);

            // Clear form on success
            if (response.success) {
                console.log('MAIL SKICKAT! Formulär rensas.');
                setFormData({
                    name: '',
                    email: '',
                    message: '',
                });
            }
        } catch (error) {
            // Handle submission errors
            console.error('FEL vid inskickning:', error);
            const errorMessage = error instanceof Error ? error.message : 'Something went wrong';
            setServerResponse({
                success: false,
                message: errorMessage,
            });
        } finally {
            setIsSubmitting(false);
            console.log('=== Formulär-submission klar ===');
        }
    };

    // Reset form to initial state
    const resetForm = () => {
        setIsSubmitted(false);
        setServerResponse(null);
        setErrors({});
        setFormData({
            name: '',
            email: '',
            message: '',
        });
    };

    // Show success message after submission
    if (isSubmitted && serverResponse?.success) {
        return (
            <div className="max-w-2xl mx-auto p-8">
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                    <div className="mb-4">
                        <svg className="mx-auto h-16 w-16 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-green-900 mb-2">
                        Message Sent Successfully!
                    </h3>
                    <p className="text-green-700 mb-4">
                        {serverResponse.message}
                    </p>
                    <button
                        onClick={resetForm}
                        className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors duration-200"
                    >
                        Send Another Message
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto p-8">
            <div className="text-center mb-8">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                    Let's Work Together
                </h2>
                <p className="text-lg text-gray-600 dark:text-gray-300">
                    Have a project in mind? I'd love to hear from you. Send me a message and I'll get back to you soon.
                </p>
            </div>

            {/* Show server error if submission failed */}
            {isSubmitted && serverResponse && !serverResponse.success && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center">
                        <svg className="h-5 w-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-red-700">
                            {serverResponse.message}
                        </p>
                    </div>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name Field */}
                <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Your Name *
                    </label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors duration-200 ${
                            errors.name
                                ? 'border-red-500 bg-red-50'
                                : 'border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white'
                        }`}
                        placeholder="Enter your full name"
                        disabled={isSubmitting}
                    />
                    {errors.name && (
                        <p className="mt-2 text-sm text-red-600 flex items-center">
                            <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {errors.name}
                        </p>
                    )}
                </div>

                {/* Email Field */}
                <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Email Address *
                    </label>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors duration-200 ${
                            errors.email
                                ? 'border-red-500 bg-red-50'
                                : 'border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white'
                        }`}
                        placeholder="your.email@example.com"
                        disabled={isSubmitting}
                    />
                    {errors.email && (
                        <p className="mt-2 text-sm text-red-600 flex items-center">
                            <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {errors.email}
                        </p>
                    )}
                </div>

                {/* Message Field */}
                <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Your Message *
                    </label>
                    <textarea
                        id="message"
                        name="message"
                        rows={6}
                        value={formData.message}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors duration-200 resize-vertical ${
                            errors.message
                                ? 'border-red-500 bg-red-50'
                                : 'border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white'
                        }`}
                        placeholder="Tell me about your project, ideas, or just say hello..."
                        disabled={isSubmitting}
                    />
                    <div className="mt-2 flex justify-between items-center">
                        {errors.message ? (
                            <p className="text-sm text-red-600 flex items-center">
                                <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                {errors.message}
                            </p>
                        ) : (
                            <div></div>
                        )}
                        <p className="text-sm text-gray-500">
                            {formData.message.length}/2000
                        </p>
                    </div>
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className={`w-full px-8 py-4 rounded-lg font-semibold text-white transition-all duration-200 ${
                            isSubmitting
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-primary hover:bg-blue-700 focus:ring-2 focus:ring-primary focus:ring-offset-2 transform hover:scale-105'
                        }`}
                    >
                        {isSubmitting ? (
                            <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Sending Message...
              </span>
                        ) : (
                            'Send Message'
                        )}
                    </button>
                </div>
            </form>

            {/* Contact Info */}
            <div className="mt-12 text-center">
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                    Or reach out to me directly:
                </p>
                <div className="flex justify-center space-x-8">
                    <a
                        href="mailto:mail@christerbengtsson.se"
                        className="flex items-center text-primary hover:text-blue-700 transition-colors duration-200"
                    >
                        <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        Email
                    </a>
                    <a
                        href="https://www.linkedin.com/in/christerben/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center text-primary hover:text-blue-700 transition-colors duration-200"
                    >
                        <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                        </svg>
                        LinkedIn
                    </a>
                </div>
            </div>
        </div>
    );
};
export default ContactForm;